import { ExpandLess, ExpandMore, OpenInNew } from '@mui/icons-material';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import DietQualityBar from '../../../components/DietQualityBar/DietQualityBar';
import { prettyDateLong } from '../../../lib/helpers/helpers';
import { getDietQualityColor } from '../../../lib/themes/generalTheme';
import SmartTooltipChip from '../../../components/SmartTooltipChip';

const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    display: 'flex',
    flexDirection: 'column',
    height: 'auto',
    minHeight: 'min-content',
    padding: 0,
    paddingBottom: theme.spacing(2),
    margin: 0,
    boxShadow: 'none',
    cursor: 'default',
    resize: 'none',
  },

  dietDetailsRow: {
    marginTop: 12,
    resize: 'none',
    maxWidth: '360px',
    '& .MuiGrid-container': {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      resize: 'none',
      gap: '4px',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'center',
        width: '100%',
        overflow: 'hidden',
      },
    },
    '& .MuiTypography-root': {
      '&.value': {
        color: theme.palette.text.primary,
      },
      lineHeight: '18px',
    },
    '& .MuiChip-root': {
      marginLeft: 'auto',
      whiteSpace: 'nowrap',
      lineHeight: '18px',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      width: '100%',
    },
  },
  dietQualityReportTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap',
    flexWrap: 'wrap',
    resize: 'none',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  dietQualityReportTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    lineHeight: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'normal',
    '& .MuiIconButton-root': {
      marginLeft: '4px',
      padding: 0,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    color: theme.palette.admin.primaryLight,
  },
  dietQualityReportTitleLink: {
    textDecoration: 'underline',
    textDecorationStyle: 'solid',
    textDecorationSkipInk: 'auto',
    textDecorationThickness: 'auto',
    cursor: 'pointer',
  },
  dietQualityReportIcon: {
    margin: 0,
    fontSize: '20px',
    lineHeight: '32px',
    color: theme.palette.admin.primaryLight,
    width: 24,
    height: 24,
    cursor: 'pointer',
  },
  limitedWidthChip: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '180px',
      width: 'auto',
    },
  },
  chipGridItem: {
    maxWidth: '100%',
    width: 'auto',
    flexShrink: 1,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 'auto',
      flexShrink: 0,
      width: 'auto',
      maxWidth: '60%',
      textAlign: 'right',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
}));

const noData = 'N/A';
export const getNoData = () => {
  return <Typography sx={{ color: '#1C124D' }}>{noData}</Typography>;
};

const getChipColor = (value) => {
  return getDietQualityColor(value, '80');
};

/**
 * Weight is dislayed neutrally. If the weight is higher or lower, it is not good nor bad.
 *
 * @param {*} currentWeight
 * @param {*} pastWeight
 * @returns current weight and icon to represent up/down
 */
export const getWeightData = (currentWeight = 0, pastWeight = 0) => {
  if (isNaN(currentWeight) || isNaN(pastWeight) || (!currentWeight && !pastWeight)) return getNoData();
  pastWeight = Number(pastWeight);
  currentWeight = Number(currentWeight);

  if (currentWeight === pastWeight || currentWeight === 0 || pastWeight === 0) {
    return (
      <Typography
        variant="body1"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
        }}
      >
        {currentWeight}
      </Typography>
    );
  }

  const currentGreaterThanPast = currentWeight > pastWeight;
  const icon = currentGreaterThanPast ? (
    <ExpandLess sx={{ width: 18, height: 18 }} />
  ) : (
    <ExpandMore sx={{ width: 18, height: 18 }} />
  );

  return (
    <Typography
      variant="body1"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
      }}
    >
      {currentWeight} {icon}
    </Typography>
  );
};

/**
 * Get the trend of the diet quality data
 *
 * @param {number} pre - The previous diet quality
 * @param {number} post - The current diet quality
 * @param {boolean} isComparison - Whether to show the full comparison i.e., 97 (+71) ^
 * @returns {React.ReactNode} - The trend of the diet quality
 */
export const getTrend = (pre = 0, post = 0, isComparison = false) => {
  if (isNaN(pre) || isNaN(post) || (!pre && !post)) return getNoData();

  pre = Number(pre);
  post = Number(post);

  if (!post || pre === post) {
    return (
      <Typography
        variant="body1"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          color: '#1C124D',
        }}
      >
        {pre}
      </Typography>
    );
  }

  const diff = Math.abs(post - pre);
  const sign = pre > post ? '-' : '+';
  const isImproving = post > pre;
  const icon = isImproving ? (
    <ExpandLess
      color="success"
      sx={{ width: 18, height: 18 }}
    />
  ) : (
    <ExpandMore
      color="error"
      sx={{ width: 18, height: 18 }}
    />
  );

  return (
    <Typography
      variant="body1"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        color: isImproving ? 'success.main' : 'error.main',
      }}
    >
      {isComparison ? `${post} (${sign}${diff})` : `${sign}${diff}`} {icon}
    </Typography>
  );
};

/**
 * Patient Diet Quality Report
 * Does not get it's own data from the API
 * Gets data from the data prop and data passed in from the parent component
 */

const PatientDietQualityReport = ({ data }) => {
  const classes = useStyles();
  const theme = useTheme();

  const isProvider = data?.isProvider || false;

  let originalHei = null;
  let lastHei = null;

  if (data?.assessmentData && data?.assessmentData?.length > 0) {
    // if this is a provider view, we need to get the HEI data from the data.assessmentData.
    if (isProvider) {
      originalHei = data?.assessmentData[0]?.dietHEI ?? null;
      lastHei = data?.assessmentData?.length > 1 ? data.assessmentData[1]?.dietHEI ?? null : null;
    }
    /**
     * There are several datapoints that need to be normalized
     * We are using lastQuality and goalQuality to determine the current score and goal score.
     * When they aren't present, we can fallback to assessment data.
     */
    if (!data?.lastQuality) {
      data.lastQuality = data?.assessmentData?.[0]?.dietQuality ?? null;
    }
    if (!data?.goalQuality) {
      data.goalQuality = data?.assessmentData?.[0]?.idealQuality ?? null;
    }
  }

  return (
    <Box
      className={classes.patientChartCard}
      sx={{
        width: '100%',
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
        >
          {isProvider && (
            <Box
              className={classes.dietQualityReportTitleContainer}
              onClick={() =>
                data?.workflowShareUrl && window.open(data?.workflowShareUrl, '_blank', 'noopener,noreferrer')
              }
            >
              <Typography
                className={`${data?.workflowShareUrl ? classes.dietQualityReportTitleLink : ''} ${
                  classes.dietQualityReportTitle
                }`}
              >
                <span>
                  Diet Quality Report
                  {data?.workflowShareUrl && (
                    <IconButton sx={{ padding: 0 }}>
                      <OpenInNew className={classes.dietQualityReportIcon} />
                    </IconButton>
                  )}
                </span>
              </Typography>
            </Box>
          )}

          {/* Diet Quality Bar */}
          <Grid
            item
            xs={12}
            className={classes.dietDetailsRow}
          >
            {!data?.lastQuality && !data?.goalQuality ? (
              <Typography
                variant="body1"
                style={{
                  lineHeight: '24px',
                }}
              >
                Patient has not completed a diet assessment.
              </Typography>
            ) : (
              ''
            )}
          </Grid>
          <Grid
            item
            xs={12}
            className={classes.dietDetailsRow}
            style={{ height: data?.lastQuality || data?.goalQuality ? 'auto' : '41px' }}
          >
            <DietQualityBar
              currentScore={data?.lastQuality || null}
              goalScore={data?.goalQuality || null}
              size="small"
            />
          </Grid>

          {/* Diet Score details */}
          {/* HEI Score */}
          {isProvider && (
            <Grid
              item
              xs={12}
              className={classes.dietDetailsRow}
            >
              <Grid container>
                <Grid item>
                  <Typography variant="body1">HEI Score:</Typography>
                </Grid>
                {<Grid item>{getTrend(originalHei, lastHei, true)}</Grid>}
              </Grid>
            </Grid>
          )}

          {/* Latest Diet Quality */}
          <Grid
            item
            xs={12}
            className={classes.dietDetailsRow}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  [theme.breakpoints.down('sm')]: {
                    width: 'auto',
                    flexShrink: 0,
                    maxWidth: '40%',
                  },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  Latest Diet Quality:
                </Typography>
              </Grid>
              <Grid
                item
                className={classes.chipGridItem}
              >
                {!data ||
                data.lastWorkflowDate === null ||
                data.lastWorkflowDate === '' ||
                data.lastWorkflowDate === undefined ? (
                  getNoData()
                ) : (
                  <SmartTooltipChip
                    className={`${classes.limitedWidthChip} ${theme.statusChip || ''}`}
                    label={prettyDateLong(data.lastWorkflowDate)}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* Current Diet */}
          <Grid
            item
            xs={12}
            className={classes.dietDetailsRow}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  [theme.breakpoints.down('sm')]: {
                    width: 'auto',
                    flexShrink: 0,
                    maxWidth: '40%',
                  },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  Current Diet:
                </Typography>
              </Grid>
              <Grid
                item
                className={classes.chipGridItem}
              >
                {!data || data.dietQuality === null || data.dietQuality === '' || isNaN(Number(data.dietQuality)) ? (
                  getNoData()
                ) : (
                  <SmartTooltipChip
                    className={`${classes.limitedWidthChip} ${theme.statusChip || ''}`}
                    style={{ backgroundColor: getChipColor(Number(data.dietQuality)) }}
                    label={`${data.dietName} - ${data.dietQuality}`}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* Goal Diet */}
          <Grid
            item
            xs={12}
            className={classes.dietDetailsRow}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  [theme.breakpoints.down('sm')]: {
                    width: 'auto',
                    flexShrink: 0,
                    maxWidth: '40%',
                  },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  Goal Diet:
                </Typography>
              </Grid>
              <Grid
                item
                className={classes.chipGridItem}
              >
                {!data || data.idealQuality === null || data.idealQuality === '' || isNaN(Number(data.idealQuality)) ? (
                  getNoData()
                ) : (
                  <SmartTooltipChip
                    className={`${classes.limitedWidthChip} ${theme.statusChip || ''}`}
                    style={{ backgroundColor: getChipColor(Number(data.idealQuality)) }}
                    label={`${data.idealName} - ${data.idealQuality}`}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* Diet Quality Trend */}
          <Grid
            item
            xs={12}
            className={classes.dietDetailsRow}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  [theme.breakpoints.down('sm')]: {
                    width: 'auto',
                    flexShrink: 0,
                    maxWidth: '40%',
                  },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  Diet Quality Trend:
                </Typography>
              </Grid>
              <Grid
                item
                className={classes.chipGridItem}
              >
                {getTrend(data?.idealQuality, data?.dietQuality, false)}
              </Grid>
            </Grid>
          </Grid>

          {/* Last Check-In */}
          {isProvider && (
            <Grid
              item
              xs={12}
              className={classes.dietDetailsRow}
            >
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm="auto"
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      width: 'auto',
                      flexShrink: 0,
                      maxWidth: '40%',
                    },
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      [theme.breakpoints.down('sm')]: {
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    Last Check-In:
                  </Typography>
                </Grid>
                <Grid
                  item
                  className={classes.chipGridItem}
                >
                  {!data ||
                  data.checkinHistoryDate === null ||
                  data.checkinHistoryDate === '' ||
                  data.checkinHistoryDate === undefined ? (
                    getNoData()
                  ) : (
                    <SmartTooltipChip
                      className={`${classes.limitedWidthChip} ${theme.statusChip || ''}`}
                      label={prettyDateLong(data.checkinHistoryDate)}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PatientDietQualityReport;
